// Color themes, adapted from the Habits app (public/js/themes.js), which took them from Marker:
// the same presets, pride themes made from flag stripes, and the arithmetic that turns a small
// palette into every CSS token while keeping text readable. Pure: no DOM.
//
// The default theme ("Relationships") sets no tokens at all, so styles.css applies as written.
// Any other theme writes the tokens from paletteTokens() onto <html> (see applyTheme in App).

export interface RGB {
  r: number;
  g: number;
  b: number;
}

/** syntax: red, orange, green, teal, blue, purple, pink, gray. */
export interface Palette {
  background: string;
  surface: string;
  text: string;
  muted: string;
  accent: string;
  syntax: string[];
}

export interface Theme {
  id: string;
  name: string;
  light: Palette | null;
  dark: Palette | null;
  /** Pride themes: the flag, top to bottom. */
  stripes?: string[];
}

// ---------------------------------------------------------------- color math

export function rgb(hexStr: string): RGB | null {
  let text = String(hexStr || '').trim().replace(/^#/, '');
  if (text.length === 3) text = [...text].map((c) => c + c).join('');
  if (!/^[0-9a-f]{6}$/i.test(text)) return null;
  const v = parseInt(text, 16);
  return { r: ((v >> 16) & 255) / 255, g: ((v >> 8) & 255) / 255, b: (v & 255) / 255 };
}

export function hex({ r, g, b }: RGB): string {
  const ch = (v: number) => Math.round(Math.max(0, Math.min(1, v)) * 255).toString(16).padStart(2, '0');
  return `#${ch(r)}${ch(g)}${ch(b)}`;
}

const rgba = (c: RGB, a: number) => `rgba(${Math.round(c.r * 255)}, ${Math.round(c.g * 255)}, ${Math.round(c.b * 255)}, ${a})`;
const WHITE: RGB = { r: 1, g: 1, b: 1 };
const BLACK: RGB = { r: 0, g: 0, b: 0 };
const NEAR_BLACK: RGB = { r: 17 / 255, g: 17 / 255, b: 17 / 255 };

export function luminance({ r, g, b }: RGB): number {
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export const mix = (a: RGB, b: RGB, t: number): RGB => ({ r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t });

export function contrast(a: RGB, b: RGB): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

function saturation({ r, g, b }: RGB): number {
  const hi = Math.max(r, g, b);
  const lo = Math.min(r, g, b);
  if (hi <= lo) return 0;
  const l = (hi + lo) / 2;
  return (hi - lo) / (1 - Math.abs(2 * l - 1));
}

function hue({ r, g, b }: RGB): number {
  const hi = Math.max(r, g, b);
  const lo = Math.min(r, g, b);
  const range = hi - lo;
  if (range <= 0) return 0;
  let d: number;
  if (hi === r) d = ((g - b) / range) % 6;
  else if (hi === g) d = (b - r) / range + 2;
  else d = (r - g) / range + 4;
  d *= 60;
  return d < 0 ? d + 360 : d;
}

function lightness({ r, g, b }: RGB): number {
  return (Math.max(r, g, b) + Math.min(r, g, b)) / 2;
}

export function hsl(h: number, s: number, l: number): RGB {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return { r: f(0), g: f(8), b: f(4) };
}

function hueDistance(color: RGB, degrees: number): number {
  const d = Math.abs(hue(color) - degrees) % 360;
  return Math.min(d, 360 - d);
}

/** `color`, pushed away from `background` until it reads there at `ratio`. */
export function readable(color: RGB, background: RGB, ratio = 4.5): RGB {
  const toward = luminance(background) > 0.4 ? BLACK : WHITE;
  let out = color;
  let step = 0;
  while (contrast(out, background) < ratio && step < 1) {
    step += 0.04;
    out = mix(color, toward, step);
  }
  return out;
}

/**
 * readable() against several backgrounds. On light ones it darkens by lightness alone, so a pastel
 * stripe (trans pink) turns a deeper pink rather than a dusty one. On dark ones it mixes toward
 * white as readable() does: raising lightness alone turns purples neon.
 */
function vivid(color: RGB, backgrounds: RGB[], ratio: number): RGB {
  const reads = (c: RGB) => backgrounds.every((b) => contrast(c, b) >= ratio);
  let out = color;
  if (luminance(backgrounds[0]) > 0.4) {
    const h = hue(color);
    const s = saturation(color);
    for (let l = lightness(color); !reads(out) && l > 0; ) {
      l = Math.max(0, l - 0.01);
      out = hsl(h, s, l);
    }
  }
  return backgrounds.reduce((c, b) => readable(c, b, ratio), out);
}

const same = (a: RGB, b: RGB) => hex(a) === hex(b);
/** Text on a filled control: whichever of white / near-black reads better. */
const onColor = (c: RGB) => (contrast(c, WHITE) >= contrast(c, NEAR_BLACK) ? '#ffffff' : '#111111');

// ---------------------------------------------------------------- presets

const palette = (background: string, surface: string, text: string, muted: string, accent: string, syntax: string[]): Palette => ({
  background,
  surface,
  text,
  muted,
  accent,
  syntax,
});

export const DEFAULT_THEME_ID = 'relationships';

/** This app's own look (styles.css). Its palettes only draw the preview card. */
const DEFAULT: Theme = {
  id: DEFAULT_THEME_ID,
  name: 'Relationships',
  light: palette('#f7f6f3', '#ffffff', '#1f1d1a', '#6b665d', '#5b4fd6', ['#c2372b', '#a15c00', '#1c7c4c', '#0f8a7e', '#2f6fd6', '#8b5cf6', '#b0569a', '#8a8478']),
  dark: palette('#161514', '#201f1d', '#edeae4', '#a8a299', '#8f85ff', ['#ff7a6e', '#f0b04f', '#5fd39a', '#3fc2b4', '#6ea2ff', '#b292ff', '#e07cc8', '#8f897f']),
};

export const PRESETS: Theme[] = [
  DEFAULT,
  {
    id: 'habits',
    name: 'Habits',
    light: palette('#f4f4f8', '#ffffff', '#1c1c2e', '#666666', '#3d35b5', ['#b52020', '#b45309', '#4caf7d', '#0e7490', '#2158c9', '#5b4fcf', '#b33591', '#888888']),
    dark: palette('#12121c', '#1c1c2b', '#e9e8f5', '#b4b3c8', '#9a93ff', ['#ff8a8a', '#e0a253', '#4caf7d', '#55c4d8', '#85a9ff', '#a69fff', '#ff9ede', '#8d95a1']),
  },
  {
    id: 'marker',
    name: 'Marker',
    light: palette('#ffffff', '#fbfbfc', '#1b1d21', '#6a707c', '#3d63dd', ['#c4403c', '#b45309', '#187f4a', '#0e7490', '#2158c9', '#7141c4', '#b33591', '#8a9099']),
    dark: palette('#17191d', '#1b1e23', '#e3e6eb', '#99a1ad', '#7d9cff', ['#ff8079', '#e0a253', '#5fcf95', '#55c4d8', '#85a9ff', '#c3a0ff', '#ff9ede', '#8d95a1']),
  },
  {
    id: 'paper',
    name: 'Paper',
    light: palette('#f7f1e3', '#efe7d4', '#3b3025', '#7a6a58', '#a4502a', ['#b03a2e', '#b0651b', '#5b7f2d', '#2e7d73', '#3d6a9e', '#7d4e8f', '#a8436e', '#8f8374']),
    dark: palette('#221c16', '#2a231c', '#e8dcc8', '#a8987f', '#e0955f', ['#e0776a', '#dd9a52', '#9cbf6b', '#72b8a8', '#86a8d8', '#c19ad2', '#e08aad', '#9c8f7e']),
  },
  {
    id: 'solarized',
    name: 'Solarized',
    light: palette('#fdf6e3', '#eee8d5', '#586e75', '#839496', '#268bd2', ['#dc322f', '#cb4b16', '#859900', '#2aa198', '#268bd2', '#6c71c4', '#d33682', '#93a1a1']),
    dark: palette('#002b36', '#073642', '#93a1a1', '#657b83', '#2aa198', ['#dc322f', '#cb4b16', '#859900', '#2aa198', '#268bd2', '#6c71c4', '#d33682', '#586e75']),
  },
  {
    id: 'nord',
    name: 'Nord',
    light: palette('#eceff4', '#e5e9f0', '#2e3440', '#4c566a', '#5e81ac', ['#bf616a', '#d08770', '#6f8f4f', '#5e9e9b', '#5e81ac', '#8f6f94', '#a9678f', '#7b88a1']),
    dark: palette('#2e3440', '#3b4252', '#d8dee9', '#8891a3', '#88c0d0', ['#bf616a', '#d08770', '#a3be8c', '#8fbcbb', '#81a1c1', '#b48ead', '#c895bf', '#616e88']),
  },
  {
    id: 'dracula',
    name: 'Dracula',
    light: null,
    dark: palette('#282a36', '#2f3241', '#f8f8f2', '#9aa0c3', '#bd93f9', ['#ff5555', '#ffb86c', '#50fa7b', '#8be9fd', '#8aa9f9', '#bd93f9', '#ff79c6', '#6272a4']),
  },
  {
    id: 'github',
    name: 'GitHub',
    light: palette('#ffffff', '#f6f8fa', '#1f2328', '#59636e', '#0969da', ['#cf222e', '#bc4c00', '#116329', '#0e7c86', '#0550ae', '#8250df', '#bf3989', '#6e7781']),
    dark: palette('#0d1117', '#161b22', '#e6edf3', '#8d96a0', '#4493f8', ['#ff7b72', '#ffa657', '#7ee787', '#39c5cf', '#79c0ff', '#d2a8ff', '#ff9bce', '#8b949e']),
  },
  {
    id: 'gruvbox',
    name: 'Gruvbox',
    light: palette('#fbf1c7', '#f2e5bc', '#3c3836', '#7c6f64', '#076678', ['#9d0006', '#af3a03', '#79740e', '#427b58', '#076678', '#8f3f71', '#b16286', '#928374']),
    dark: palette('#282828', '#32302f', '#ebdbb2', '#a89984', '#83a598', ['#fb4934', '#fe8019', '#b8bb26', '#8ec07c', '#83a598', '#d3869b', '#e7a0b8', '#928374']),
  },
  {
    id: 'contrast',
    name: 'High Contrast',
    light: palette('#ffffff', '#f2f2f2', '#000000', '#3a3a3a', '#0033cc', ['#b00000', '#8a4b00', '#006400', '#005f73', '#0033cc', '#5a1ea6', '#a0006e', '#555555']),
    dark: palette('#000000', '#111111', '#ffffff', '#d0d0d0', '#7fb2ff', ['#ff6b6b', '#ffb347', '#5cff8a', '#4de8ff', '#7fb2ff', '#d4a5ff', '#ff8ad8', '#b0b0b0']),
  },
];

// ---------------------------------------------------------------- pride themes (from flag stripes)

/** A flag's colors, top to bottom, without its whites, blacks and grays or repeats. */
function flagColors(stripes: string[], fallback: RGB): RGB[] {
  const found: RGB[] = [];
  for (const s of stripes.map((x) => rgb(x) ?? BLACK)) if (saturation(s) > 0.2 && !found.some((c) => same(c, s))) found.push(s);
  return found.length ? found : [fallback];
}

const LINE_HUES = [0, 30, 125, 180, 220, 275, 325]; // red … pink on the color wheel

interface Roles {
  page: number;
  panels: number;
  accent: number;
}

/** Light and dark palettes made from a flag, as Marker makes a pride theme. */
function palettesFrom(stripes: string[], r: Roles): { light: Palette; dark: Palette } {
  const flag = stripes.map((s) => rgb(s) ?? BLACK);
  const pick = (i: number) => flag[Math.min(Math.max(i, 0), flag.length - 1)];
  const found = flagColors(stripes, pick(r.accent));
  const make = (dark: boolean): Palette => {
    const background = mix(dark ? rgb('#15161a')! : WHITE, pick(r.page), dark ? 0.12 : 0.06);
    const surface = mix(dark ? rgb('#1c1d22')! : WHITE, pick(r.panels), dark ? 0.2 : 0.13);
    const text = mix(dark ? rgb('#ececf1')! : rgb('#1d1e23')!, pick(r.page), 0.07);
    const muted = mix(dark ? rgb('#a6a8b2')! : rgb('#5d606a')!, pick(r.page), 0.16);
    const lines = LINE_HUES.map((h) => hex(readable(found.reduce((a, b) => (hueDistance(b, h) < hueDistance(a, h) ? b : a)), surface)));
    const gray = (dark ? DEFAULT.dark! : DEFAULT.light!).syntax[7];
    return { background: hex(background), surface: hex(surface), text: hex(text), muted: hex(muted), accent: hex(readable(pick(r.accent), background)), syntax: [...lines, gray] };
  };
  return { light: make(false), dark: make(true) };
}

const flag = (id: string, name: string, stripes: string[], roles: Roles): Theme => ({ id: `pride-${id}`, name, stripes, ...palettesFrom(stripes, roles) });

export const PRIDE: Theme[] = [
  flag('rainbow', 'Rainbow', ['#e40303', '#ff8c00', '#ffed00', '#008026', '#24408e', '#732982'], { page: 5, panels: 4, accent: 4 }),
  flag('trans', 'Trans', ['#5bcefa', '#f5a9b8', '#ffffff', '#f5a9b8', '#5bcefa'], { page: 1, panels: 0, accent: 0 }),
  flag('lesbian', 'Lesbian', ['#d52d00', '#ef7627', '#ff9a56', '#ffffff', '#d162a4', '#b55690', '#a30262'], { page: 4, panels: 1, accent: 6 }),
  flag('bisexual', 'Bisexual', ['#d60270', '#d60270', '#9b4f96', '#0038a8', '#0038a8'], { page: 2, panels: 3, accent: 0 }),
  flag('pansexual', 'Pansexual', ['#ff218c', '#ffd800', '#21b1ff'], { page: 0, panels: 2, accent: 0 }),
  flag('gay', 'Gay', ['#078d70', '#26ceaa', '#98e8c1', '#ffffff', '#7bade2', '#5049cc', '#3d1a78'], { page: 1, panels: 4, accent: 5 }),
  flag('genderfluid', 'Genderfluid', ['#ff76a4', '#ffffff', '#c011d7', '#000000', '#2f3cbe'], { page: 0, panels: 4, accent: 2 }),
  flag('genderqueer', 'Genderqueer', ['#b57edc', '#ffffff', '#4a8123'], { page: 0, panels: 2, accent: 0 }),
  flag('nonbinary', 'Non-binary', ['#fcf434', '#ffffff', '#9c59d1', '#2c2c2c'], { page: 2, panels: 0, accent: 2 }),
  flag('asexual', 'Asexual', ['#000000', '#a3a3a3', '#ffffff', '#800080'], { page: 3, panels: 1, accent: 3 }),
  flag('aromantic', 'Aromantic', ['#3da542', '#a7d379', '#ffffff', '#a9a9a9', '#000000'], { page: 1, panels: 0, accent: 0 }),
  flag('agender', 'Agender', ['#000000', '#b9b9b9', '#ffffff', '#b8f483', '#ffffff', '#b9b9b9', '#000000'], { page: 3, panels: 1, accent: 3 }),
  flag('intersex', 'Intersex', ['#ffd800', '#7902aa', '#ffd800'], { page: 1, panels: 0, accent: 1 }),
];

export const ALL_THEMES: Theme[] = [...PRESETS, ...PRIDE];

export function findTheme(id: string | undefined): Theme {
  return ALL_THEMES.find((t) => t.id === id) ?? DEFAULT;
}

/** The palette to use for a theme in light or dark; a one-way theme (Dracula) ignores `dark`. */
export const paletteOf = (theme: Theme, dark: boolean): Palette => (dark ? theme.dark ?? theme.light! : theme.light ?? theme.dark!);

/** true/false for a theme that only comes dark/light; null if it has both. */
export function forcedDark(theme: Theme): boolean | null {
  if (!theme.light && theme.dark) return true;
  if (!theme.dark && theme.light) return false;
  return null;
}

const stops = (colors: string[]) => {
  const step = 100 / colors.length;
  return colors.map((c, i) => `${c} ${(i * step).toFixed(3)}% ${((i + 1) * step).toFixed(3)}%`).join(', ');
};

/** Hard-stop gradient of a flag's stripes. */
export const stripeGradient = (stripes: string[], direction = '90deg'): string => `linear-gradient(${direction}, ${stops(stripes)})`;

/** The stripes around a circle, starting at the top. */
export const stripeRing = (stripes: string[]): string => `conic-gradient(${stops(stripes)})`;

/** How many flag colors styles.css cycles through. Flags have 1, 2, 3 or 6 colors, so every flag repeats evenly. */
export const FLAG_SLOTS = 6;

/** The CSS class that colors a nav item, list item or chart mark with the flag's nth color (counting from 0). */
export const flagSlot = (n: number): string => `flag-${(n % FLAG_SLOTS) + 1}`;

/**
 * A pride theme's colors as tokens, so icons, tabs and marks can each take a different flag color:
 * --flag-N for icons and marks (3:1 on the panels and page), --flag-N-soft for tints behind them,
 * and --flag-N-text for words, readable on either.
 */
export function flagTokens(stripes: string[], p: Palette): Record<string, string> {
  const bg = rgb(p.background) ?? WHITE;
  const surface = rgb(p.surface) ?? bg;
  const dark = luminance(bg) < 0.4;
  const colors = flagColors(stripes, rgb(p.accent) ?? BLACK);
  const page = paletteTokens(p);
  const text = rgb(page['--text'])!;
  const muted = rgb(page['--text-muted'])!;
  const base = dark ? rgb('#1c1d22')! : WHITE;
  const out: Record<string, string> = {};
  // Yellow darkened for a light page turns olive; turned toward orange first, it turns gold.
  const warm = (c: RGB) => (!dark && hue(c) > 47 && hue(c) < 72 ? hsl(45, saturation(c), lightness(c)) : c);
  for (let i = 0; i < FLAG_SLOTS; i++) {
    const ink = vivid(warm(colors[i % colors.length]), [surface, bg], 3);
    // Tinted from a neutral panel, not the theme's own (already tinted by one stripe), so each color shows true;
    // lighter where a bright color (yellow on dark) would leave muted text hard to read on a tinted card.
    let amount = dark ? 0.24 : 0.14;
    let soft = mix(base, ink, amount);
    while (amount > 0.04 && !(contrast(muted, soft) >= 4.6 && contrast(text, soft) >= 4.6)) {
      amount -= 0.01;
      soft = mix(base, ink, amount);
    }
    out[`--flag-${i + 1}`] = hex(ink);
    out[`--flag-${i + 1}-soft`] = hex(soft);
    out[`--flag-${i + 1}-text`] = hex(vivid(ink, [soft, surface], 4.5));
  }
  return out;
}

// ---------------------------------------------------------------- palette → this app's tokens

/** Every color token styles.css defines, worked out from a palette and pushed to readable contrast. */
export function paletteTokens(p: Palette): Record<string, string> {
  const bg = rgb(p.background) ?? WHITE;
  const surface = rgb(p.surface) ?? bg;
  const dark = luminance(bg) < 0.4;
  // Body text must read on both the page and the panels (Solarized light is 4.4:1 as designed).
  const text = readable(readable(rgb(p.text) ?? BLACK, surface, 4.5), bg, 4.5);
  const muted = readable(rgb(p.muted) ?? mix(text, bg, 0.4), surface, 4.5);
  const s = p.syntax.map((c) => rgb(c));
  const accent = readable(rgb(p.accent) ?? rgb(DEFAULT.light!.accent)!, surface, 3.2);
  const danger = readable(s[0] ?? rgb('#c2372b')!, surface, 4.5);
  const warn = readable(s[1] ?? rgb('#a15c00')!, surface, 4.5);
  const success = readable(s[2] ?? rgb('#1c7c4c')!, surface, 3);
  const card = luminance(surface) >= luminance(bg) ? surface : bg;
  // Chart lines are drawn on the page, next to cards in the panel color.
  const line = (c: RGB | null, fallback: string) => hex(readable(c ?? rgb(fallback)!, bg, 2.6));
  return {
    '--bg': hex(bg),
    '--bg-elev': hex(surface),
    '--bg-sunken': hex(mix(bg, dark ? BLACK : text, dark ? 0.25 : 0.035)),
    '--bg-hover': hex(mix(surface, text, dark ? 0.08 : 0.06)),
    '--border': hex(mix(surface, text, dark ? 0.14 : 0.12)),
    '--border-strong': hex(mix(surface, text, dark ? 0.26 : 0.22)),
    '--text': hex(text),
    '--text-muted': hex(muted),
    '--text-faint': hex(mix(muted, surface, 0.3)),
    '--accent': hex(accent),
    '--accent-hover': hex(mix(accent, text, 0.18)),
    '--accent-soft': hex(mix(surface, accent, dark ? 0.2 : 0.12)),
    '--accent-text': onColor(accent),
    '--danger': hex(danger),
    '--danger-soft': hex(mix(surface, danger, dark ? 0.16 : 0.08)),
    '--success': hex(success),
    '--warn': hex(warn),
    '--warn-soft': hex(mix(surface, warn, dark ? 0.16 : 0.1)),
    '--line': hex(readable(mix(muted, bg, 0.15), bg, 2.6)),
    '--line-partner': line(s[6], '#b0569a'),
    '--line-adoptive': line(s[4], '#2f6fd6'),
    '--line-step': line(s[7], '#8a8478'),
    '--line-foster': line(s[3], '#0f8a7e'),
    '--line-chosen': line(s[5], '#8b5cf6'),
    // Person cards use the brighter of page and panel, so they stand out on the chart.
    '--card-bg': hex(card),
    '--card-dead-bg': hex(mix(card, text, dark ? 0.05 : 0.045)),
    '--focus-ring': `0 0 0 3px ${rgba(accent, 0.35)}`,
  };
}
